import { useState, useCallback, useEffect } from "react";
import { Spin } from "antd";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import useGroups from "../hooks/useGroups";
import useExcursions from "../hooks/useExcursions";
import useCalendarTasks from "../hooks/useCalendarTasks";
import useUsers from "../hooks/useUsers";
import CalendarView from "../components/calendar/CalendarView";
import CalendarLegend from "../components/calendar/CalendarLegend";
import CalendarFilters from "../components/calendar/CalendarFilters";
import ExcursionDetailModal from "../components/calendar/ExcursionDetailModal";
import GroupsListModal from "../components/calendar/GroupsListModal";
import GroupDetailModal from "../components/calendar/GroupDetailModal";
import DayOptionsModal from "../components/calendar/DayOptionsModal";
import GroupFormModal from "../components/calendar/GroupFormModal";
import ScheduleExcursionFormModal from "../components/ScheduleExcursionFormModal";
import TaskFormModal from "../components/calendar/TaskFormModal";
import TaskDetailModal from "../components/calendar/TaskDetailModal";
import TasksList from "../components/calendar/TasksList";
import TaskRemindersModal from "../components/calendar/TaskRemindersModal";
import PageDocumentation from "../components/PageDocumentation";
import "../styles/calendar.css";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

function Calendar() {
  const { groups, getGroups, loading: groupsLoading } = useGroups();
  const { excursions, getExcursions, loading: excursionsLoading } = useExcursions();
  const { users, getUsers } = useUsers();
  const {
    tasks,
    task: taskDetail,
    pendingReminders,
    loading: tasksLoading,
    getTasks,
    getTask,
    createTask,
    updateTask,
    archiveTask,
    restoreTask,
    addComment,
    deleteComment,
    getPendingReminders,
    createRecurringTemplate,
  } = useCalendarTasks();

  const [currentDateRange, setCurrentDateRange] = useState(null);

  // Filtros del calendario
  const [filters, setFilters] = useState({
    showGroups: true,
    showExcursions: true,
    showTasks: true,
  });

  // Estados para modales
  const [excursionModalVisible, setExcursionModalVisible] = useState(false);
  const [selectedExcursion, setSelectedExcursion] = useState(null);

  const [groupsListModalVisible, setGroupsListModalVisible] = useState(false);
  const [groupsList, setGroupsList] = useState([]);
  const [groupsListType, setGroupsListType] = useState("ingreso");
  const [groupsListDate, setGroupsListDate] = useState(null);

  const [groupDetailModalVisible, setGroupDetailModalVisible] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Estados para click en dia
  const [dayOptionsVisible, setDayOptionsVisible] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  // Estados para crear excursion/grupo
  const [excursionFormVisible, setExcursionFormVisible] = useState(false);
  const [excursionFormInitialDate, setExcursionFormInitialDate] = useState(null);
  const [groupFormVisible, setGroupFormVisible] = useState(false);

  // Estados para tareas
  const [taskFormVisible, setTaskFormVisible] = useState(false);
  const [taskFormInitialDate, setTaskFormInitialDate] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetailVisible, setTaskDetailVisible] = useState(false);
  const [tasksParams, setTasksParams] = useState({ page: 1 });
  const [remindersModalVisible, setRemindersModalVisible] = useState(false);
  const [remindersChecked, setRemindersChecked] = useState(false);

  // Cargar recordatorios al montar
  useEffect(() => {
    if (!remindersChecked) {
      getPendingReminders().then((data) => {
        if (data && data.count > 0) {
          setRemindersModalVisible(true);
        }
        setRemindersChecked(true);
      });
    }
  }, [remindersChecked]);

  // Cargar usuarios (solo trabajadores de oficina) para el selector de asignacion
  useEffect(() => {
    getUsers({ user_type: "OFFICE_WORKER" });
  }, []);

  // Cargar datos cuando cambia el rango visible
  const handleDatesSet = useCallback(async (dateInfo) => {
    const start = dayjs(dateInfo.start).format("YYYY-MM-DD");
    const end = dayjs(dateInfo.end).format("YYYY-MM-DD");

    setCurrentDateRange({ start, end });

    // Cargar excursiones, grupos y tareas en paralelo
    await Promise.all([
      getExcursions({ day_after: start, day_before: end }),
      getGroups({
        check_in_date_range_after: start,
        check_in_date_range_before: end,
      }),
      getTasks({ date_after: start, date_before: end, has_date: true }),
    ]);
  }, []);

  // Refrescar datos
  const refreshData = async () => {
    if (currentDateRange) {
      await Promise.all([
        getExcursions({
          day_after: currentDateRange.start,
          day_before: currentDateRange.end,
        }),
        getGroups({
          check_in_date_range_after: currentDateRange.start,
          check_in_date_range_before: currentDateRange.end,
        }),
        getTasks({
          date_after: currentDateRange.start,
          date_before: currentDateRange.end,
          has_date: true,
        }),
      ]);
    }
  };

  // Manejar clicks en eventos del calendario
  const handleEventClick = (clickInfo) => {
    const { type, data, date } = clickInfo.event.extendedProps;

    switch (type) {
      case "excursion":
        setSelectedExcursion(data);
        setExcursionModalVisible(true);
        break;

      case "checkin":
        setGroupsList(data);
        setGroupsListType("ingreso");
        setGroupsListDate(date);
        setGroupsListModalVisible(true);
        break;

      case "checkout":
        setGroupsList(data);
        setGroupsListType("egreso");
        setGroupsListDate(date);
        setGroupsListModalVisible(true);
        break;

      case "task":
        setSelectedTask(data);
        getTask(data.id);
        setTaskDetailVisible(true);
        break;

      default:
        break;
    }
  };

  // Manejar click en un día del calendario
  const handleDateClick = (dateInfo) => {
    setSelectedDate(dateInfo.date);
    setDayOptionsVisible(true);
  };

  // Handlers para DayOptionsModal
  const handleCreateExcursion = () => {
    setDayOptionsVisible(false);
    setExcursionFormInitialDate(selectedDate);
    setExcursionFormVisible(true);
  };

  const handleCreateGroup = () => {
    setDayOptionsVisible(false);
    setGroupFormVisible(true);
  };

  const handleViewActiveGroups = () => {
    setDayOptionsVisible(false);
    // Filtrar grupos activos en la fecha seleccionada
    const allGroups = groups.results || [];
    const activeGroups = allGroups.filter((g) => {
      const date = dayjs(selectedDate);
      return (
        date.isSameOrAfter(dayjs(g.checkInDate), "day") &&
        date.isSameOrBefore(dayjs(g.checkOutDate), "day")
      );
    });
    setGroupsList(activeGroups);
    setGroupsListType("activos");
    setGroupsListDate(selectedDate);
    setGroupsListModalVisible(true);
  };

  const handleCloseDayOptions = () => {
    setDayOptionsVisible(false);
    setSelectedDate(null);
  };

  // Cerrar modal de crear excursión
  const handleCloseExcursionForm = () => {
    setExcursionFormVisible(false);
    setExcursionFormInitialDate(null);
    refreshData();
  };

  // Cerrar modal de crear grupo
  const handleGroupFormSuccess = () => {
    setGroupFormVisible(false);
    refreshData();
  };

  // Abrir detalle de grupo desde el listado
  const handleSelectGroupFromList = (group) => {
    setGroupsListModalVisible(false);
    setSelectedGroup(group);
    setGroupDetailModalVisible(true);
  };

  // Cerrar modales
  const handleCloseExcursionModal = () => {
    setExcursionModalVisible(false);
    setSelectedExcursion(null);
  };

  const handleCloseGroupsListModal = () => {
    setGroupsListModalVisible(false);
    setGroupsList([]);
  };

  const handleCloseGroupDetailModal = () => {
    setGroupDetailModalVisible(false);
    setSelectedGroup(null);
  };

  // Actualizar despues de cambios en grupo
  const handleGroupUpdate = async () => {
    await refreshData();
    // Actualizar el grupo seleccionado si existe
    if (selectedGroup) {
      const updatedGroups = groups.results || [];
      const updated = updatedGroups.find((g) => g.id === selectedGroup.id);
      if (updated) {
        setSelectedGroup(updated);
      }
    }
  };

  // === HANDLERS DE TAREAS ===

  // Crear tarea desde DayOptionsModal
  const handleCreateTask = () => {
    setDayOptionsVisible(false);
    setTaskFormInitialDate(selectedDate);
    setSelectedTask(null);
    setTaskFormVisible(true);
  };

  // Crear tarea desde la lista
  const handleCreateTaskFromList = () => {
    setSelectedTask(null);
    setTaskFormInitialDate(null);
    setTaskFormVisible(true);
  };

  // Editar tarea
  const handleEditTask = (task) => {
    setSelectedTask(task);
    setTaskFormInitialDate(null);
    setTaskFormVisible(true);
  };

  // Ver tarea
  const handleViewTask = async (task) => {
    setSelectedTask(task);
    await getTask(task.id);
    setTaskDetailVisible(true);
  };

  // Submit del formulario de tarea
  const handleTaskFormSubmit = async (values) => {
    if (selectedTask) {
      await updateTask(selectedTask.id, values);
    } else {
      await createTask(values);
    }
    setTaskFormVisible(false);
    setSelectedTask(null);
    setTaskFormInitialDate(null);
    refreshData();
  };

  // Submit del formulario de tarea periodica
  const handleRecurringTaskSubmit = async (values) => {
    await createRecurringTemplate(values);
    setTaskFormVisible(false);
    setSelectedTask(null);
    setTaskFormInitialDate(null);
    refreshData();
  };

  // Archivar tarea
  const handleArchiveTask = async (taskId) => {
    await archiveTask(taskId);
    refreshData();
  };

  // Restaurar tarea
  const handleRestoreTask = async (taskId) => {
    await restoreTask(taskId);
    refreshData();
  };

  // Agregar comentario
  const handleAddComment = async (content) => {
    if (selectedTask) {
      await addComment(selectedTask.id, content);
      await getTask(selectedTask.id);
    }
  };

  // Eliminar comentario
  const handleDeleteComment = async (commentId) => {
    await deleteComment(commentId);
    if (selectedTask) {
      await getTask(selectedTask.id);
    }
  };

  // Marcar tarea como completada
  const handleMarkTaskComplete = async (taskId) => {
    await updateTask(taskId, { status: "COMPLETED" });
    getPendingReminders();
    refreshData();
  };

  // Cerrar modales de tareas
  const handleCloseTaskForm = () => {
    setTaskFormVisible(false);
    setSelectedTask(null);
    setTaskFormInitialDate(null);
  };

  const handleCloseTaskDetail = () => {
    setTaskDetailVisible(false);
    setSelectedTask(null);
  };

  const handleEditFromDetail = () => {
    setTaskDetailVisible(false);
    setTaskFormVisible(true);
  };

  const isLoading = groupsLoading.get || excursionsLoading.get || tasksLoading.get;

  return (
    <Spin spinning={isLoading}>
      <div className="calendar-container bg-white rounded-lg p-4">
        {/* Header con documentación */}
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-semibold">Calendario</h1>
          <PageDocumentation docKey="calendar" />
        </div>

        {/* Filtros del calendario */}
        <CalendarFilters filters={filters} onChange={setFilters} />

        <CalendarLegend />

        <CalendarView
          excursions={excursions.results || []}
          groups={groups.results || []}
          tasks={tasks.results || []}
          filters={filters}
          onDatesSet={handleDatesSet}
          onEventClick={handleEventClick}
          onDateClick={handleDateClick}
        />

        {/* Modal de detalle de excursion */}
        <ExcursionDetailModal
          visible={excursionModalVisible}
          excursion={selectedExcursion}
          onClose={handleCloseExcursionModal}
        />

        {/* Modal de listado de grupos (ingreso/egreso) */}
        <GroupsListModal
          visible={groupsListModalVisible}
          groups={groupsList}
          type={groupsListType}
          date={groupsListDate}
          onClose={handleCloseGroupsListModal}
          onSelectGroup={handleSelectGroupFromList}
        />

        {/* Modal de detalle de grupo con asignacion de excursiones */}
        <GroupDetailModal
          visible={groupDetailModalVisible}
          group={selectedGroup}
          onClose={handleCloseGroupDetailModal}
          onUpdate={handleGroupUpdate}
        />

        {/* Modal de opciones al hacer click en un dia */}
        <DayOptionsModal
          visible={dayOptionsVisible}
          date={selectedDate}
          onClose={handleCloseDayOptions}
          onCreateExcursion={handleCreateExcursion}
          onCreateGroup={handleCreateGroup}
          onViewActiveGroups={handleViewActiveGroups}
          onCreateTask={handleCreateTask}
        />

        {/* Modal para crear excursion */}
        <ScheduleExcursionFormModal
          visible={excursionFormVisible}
          onCancel={handleCloseExcursionForm}
          initialDate={excursionFormInitialDate}
        />

        {/* Modal para crear grupo */}
        <GroupFormModal
          visible={groupFormVisible}
          initialDate={selectedDate}
          onClose={() => setGroupFormVisible(false)}
          onSuccess={handleGroupFormSuccess}
        />

        {/* Modal para crear/editar tarea */}
        <TaskFormModal
          visible={taskFormVisible}
          task={selectedTask}
          initialDate={taskFormInitialDate}
          onClose={handleCloseTaskForm}
          onSubmit={handleTaskFormSubmit}
          onSubmitRecurring={handleRecurringTaskSubmit}
          loading={tasksLoading.post || tasksLoading.put}
          users={users}
        />

        {/* Modal de detalle de tarea con comentarios e historial */}
        <TaskDetailModal
          visible={taskDetailVisible}
          task={taskDetail}
          onClose={handleCloseTaskDetail}
          onEdit={handleEditFromDetail}
          onAddComment={handleAddComment}
          onDeleteComment={handleDeleteComment}
          loading={tasksLoading}
        />

        {/* Modal de recordatorios pendientes */}
        <TaskRemindersModal
          visible={remindersModalVisible}
          reminders={pendingReminders.results || []}
          onClose={() => setRemindersModalVisible(false)}
          onViewTask={handleViewTask}
          onMarkComplete={handleMarkTaskComplete}
        />

        {/* Lista de todas las tareas con CRUD */}
        <TasksList
          tasks={tasks}
          loading={tasksLoading.get}
          onCreateTask={handleCreateTaskFromList}
          onEditTask={handleEditTask}
          onArchiveTask={handleArchiveTask}
          onRestoreTask={handleRestoreTask}
          onViewTask={handleViewTask}
          onRefresh={refreshData}
          params={tasksParams}
          onParamsChange={setTasksParams}
        />
      </div>
    </Spin>
  );
}

export default Calendar;
